/**
 * Привязывает поведение виджета к пользовательскому <input>. Оборачивает input
 * в контейнер `.doka-root`, предоставляет:
 *
 *   - `input` (исходный элемент, дополненный ARIA)
 *   - `container` (оборачивающий .doka-root)
 *   - `onInputDebounced(callback)` — регистрирует debounce-обработчик input
 *
 * Debounce: по атрибуту `data-debounce` (мс). По умолчанию 250.
 *
 * ARIA: input становится role=combobox aria-autocomplete=list aria-expanded=
 * (управляется состоянием через Dropdown), с aria-controls, указывающим на
 * id dropdown'а.
 *
 * Методы регистрации обработчиков (onInputDebounced/onFocus/onKeyDown)
 * возвращают функцию снятия listener'а, чтобы createWidget мог отцепить их
 * при cleanup.
 */
export class SearchBox {
    constructor(input, { debounceMs }) {
        this.input = input;
        this.debounceMs = debounceMs;
        this.debounceId = null;

        // Оборачиваем input
        this.container = document.createElement('div');
        this.container.className = 'doka-root';
        input.parentNode.insertBefore(this.container, input);
        this.container.appendChild(input);

        // ARIA + дефолты autocomplete
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('autocomplete', 'off');
    }

    /**
     * @param {(query: string) => void} callback
     * @returns {() => void} функция снятия listener'а для cleanup
     */
    onInputDebounced(callback) {
        const handler = () => {
            clearTimeout(this.debounceId);
            this.debounceId = setTimeout(() => {
                callback(this.input.value.trim());
            }, this.debounceMs);
        };
        this.input.addEventListener('input', handler);
        return () => this.input.removeEventListener('input', handler);
    }

    /** @returns {() => void} remove-listener function for cleanup */
    onFocus(callback) {
        this.input.addEventListener('focus', callback);
        return () => this.input.removeEventListener('focus', callback);
    }

    /** @returns {() => void} remove-listener function for cleanup */
    onKeyDown(callback) {
        this.input.addEventListener('keydown', callback);
        return () => this.input.removeEventListener('keydown', callback);
    }

    setExpanded(expanded) {
        this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    setActiveDescendant(id) {
        if (id) {
            this.input.setAttribute('aria-activedescendant', id);
        } else {
            this.input.removeAttribute('aria-activedescendant');
        }
    }

    setValue(value) {
        // Обновляем значение input БЕЗ диспетчеризации события input.
        // Вызывающий код (выбор suggestion, Enter с клавиатуры на suggestion) запускает
        // следующий поиск явно через run(); диспетчеризация здесь привела бы к
        // двойному срабатыванию через debounce-обработчик input примерно через 250 мс.
        this.input.value = value;
    }

    /**
     * Отменяет любой ожидающий debounce-обработчик input.
     * Вызывается из cleanup в createWidget, чтобы destroy() не оставлял
     * таймер тикать против только что развёрнутого input.
     */
    cancelDebounce() {
        if (this.debounceId !== null) {
            clearTimeout(this.debounceId);
            this.debounceId = null;
        }
    }

    /**
     * Возвращает `input` обратно из `.doka-root` и удаляет обёртку.
     * Идемпотентно — второй вызов no-op (у container нет родителя).
     */
    unwrap() {
        if (!this.container || !this.container.parentNode) return;
        const parent = this.container.parentNode;
        parent.insertBefore(this.input, this.container);
        parent.removeChild(this.container);
        // Снимаем ARIA-атрибуты, которые мы добавили в конструкторе
        this.input.removeAttribute('role');
        this.input.removeAttribute('aria-autocomplete');
        this.input.removeAttribute('aria-expanded');
        this.input.removeAttribute('aria-activedescendant');
        this.input.removeAttribute('aria-controls');
        this.input.removeAttribute('autocomplete');
    }
}
